import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MenuItem, Select, styled } from '@mui/material';
import { storeAppraisalCycleSelected } from './AppraisalCycleSlice';
import { getCurrentYear } from '../../../utils/utils';

interface AppraisalCycle {
  duration: string;
  label?: string;
}

const StyledMenuSelect = styled(Select)`
  &.menu-item-list {
    color: #848282;
    font-family: Inter;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const AppraisalsCycleMenu: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSelectedYear = useSelector((state: any) => state.appraisalCycle.selectedYear);
  const [selectedOption, setSelectedOption] = useState(currentSelectedYear ?? getCurrentYear());
  const [appraisalOptions, setAppraisalOptions] = useState([getCurrentYear()]);
  const dispatch = useDispatch();
  const joiningYear = useSelector((state: any) => state.appraisalCycle.joiningYear);
  const isAnalyticsDetailsPage = location.pathname.includes('/analytics/');
  const isUpdatingRef = React.useRef(false);

  // Extract only the year parameter to avoid depending on entire searchParams object
  const urlYear = searchParams.get('year');

  const currentYear = selectedOption ? parseInt(selectedOption.toString(), 10) : getCurrentYear();
  const nextYear = parseInt(selectedOption.toString(), 10) + 1 || currentYear + 1;

  const [appraisalCycle, setAppraisalCycle] = useState<AppraisalCycle>({
    duration: `01 April, ${currentYear} to 31 March, ${nextYear}`,
  });

  const appraisalOptionsfun = (joiningVal: Date) => {
    const options = [];
    const startyear = joiningVal.getMonth() > 2 ? joiningVal.getFullYear() : joiningVal.getFullYear() - 1;
    for (let year = startyear; year <= getCurrentYear(); year += 1) {
      options.push(year);
    }
    return options;
  };

  // Sync year from URL query params on analytics details page
  useEffect(() => {
    if (isAnalyticsDetailsPage && !isUpdatingRef.current) {
      if (urlYear) {
        const yearNum = parseInt(urlYear, 10);
        if (yearNum !== selectedOption) {
          isUpdatingRef.current = true;
          setSelectedOption(yearNum);
          dispatch(storeAppraisalCycleSelected(yearNum));
        }
      }
    } else if (!isAnalyticsDetailsPage && !isUpdatingRef.current) {
      // Sync selectedOption to Redux when not on analytics page, but only if it changed
      const effectiveSelectedYear = selectedOption || getCurrentYear();
      if (currentSelectedYear !== effectiveSelectedYear) {
        dispatch(storeAppraisalCycleSelected(effectiveSelectedYear));
      }
    }
  }, [urlYear, isAnalyticsDetailsPage, dispatch, selectedOption, currentSelectedYear]);

  // Reset the updating flag after selectedOption has been updated
  useEffect(() => {
    if (isUpdatingRef.current) {
      isUpdatingRef.current = false;
    }
  }, [selectedOption]);

  useEffect(() => {
    const fetchAppraisalCycle = async () => {
      const appLunchDate = new Date('2023-04-01');
      const empJoining = new Date(joiningYear || appLunchDate);
      const startingDate = appLunchDate.getFullYear() >= empJoining.getFullYear() ? appLunchDate : empJoining;
      setAppraisalOptions(appraisalOptionsfun(startingDate));
    };

    fetchAppraisalCycle();
  }, [joiningYear]);

  const handleOptionSelect = (option) => {
    isUpdatingRef.current = true;
    setSelectedOption(option);
    const nextYearValue = parseInt(option, 10) + 1;
    setAppraisalCycle({
      duration: `01 April, ${option} to 31 March, ${nextYearValue}`,
    });

    // Dispatch to Redux
    dispatch(storeAppraisalCycleSelected(option));

    // Update URL query params if on analytics details page
    if (isAnalyticsDetailsPage) {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set('year', option.toString());
      setSearchParams(newParams, { replace: true });
    }
  };

  return (
    <div>
      {appraisalCycle && (
        <StyledMenuSelect
          className="menu-item-list"
          value={selectedOption}
          sx={{
            boxShadow: 'none',
            marginRight: '10px',
            '.MuiOutlinedInput-notchedOutline': { border: 0 },
          }}
          placeholder="name"
        >
          {appraisalOptions.map((option) => (
            <MenuItem key={option} value={option} onClick={() => handleOptionSelect(option)}>
              Financial Year {option}-{`${option + 1}`.slice(-2)}
            </MenuItem>
          ))}
        </StyledMenuSelect>
      )}
    </div>
  );
};

export default AppraisalsCycleMenu;
